import React, { useEffect, useState } from "react";
import { AiOutlineLoading } from "react-icons/ai";

import { isPositionSelected } from "./positionHelper";
import { usePositionContext } from "../../../context/position/PositionContext";

export const ModalPosition = ({
  id,
  name,
  type,
  showPosition,
  setShowPosition,
}) => {
  const { position, createPosition, fetchPosition } = usePositionContext();

  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [localPosition, setLocalPosition] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);

  useEffect(() => {
    if (!position) fetchPosition();
    else setLocalPosition(position);
    // eslint-disable-next-line
  }, [position]);

  // Allow ESC to close (visual UX nicety; does not alter core logic)
  useEffect(() => {
    if (!showPosition) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowPosition(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showPosition, setShowPosition]);

  const save = async () => {
    setLoading(true);
    const { response, error } = await createPosition({
      id: id,
      position: selectedPosition,
      type: type,
    });

    if (response) {
      await fetchPosition();
      setSuccess(true);
      setError(false);
      setSelectedPosition(null);
    }
    if (error) {
      setError(true);
      setSuccess(false);
    }

    setLoading(false);
  };

  return (
    <div className={showPosition ? "fixed inset-0 z-[150]" : "hidden"}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />

      {/* Dialog container */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="position-title"
        className="relative mx-4 flex min-h-full items-center justify-center"
      >
        {/* Card */}
        <div className="relative w-full max-w-xl rounded-2xl border border-gray-200 bg-white p-0 shadow-2xl">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
            <div>
              <h2
                id="position-title"
                className="text-base font-semibold tracking-tight text-gray-900"
              >
                Promoting everywhere {type}s are listed
              </h2>
              <p className="mt-0.5 text-xs text-gray-600">
                Wherever {type}s are listed, this is the position this {type} will
                occupy.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowPosition(false)}
              aria-label="Close"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="px-5 pb-5 pt-4">
            {/* Subject */}
            <div className="mb-3">
              <h3 className="text-sm font-semibold text-gray-900">
                {type}:{" "}
                <span className="text-emerald-700">{name}</span>
              </h3>
            </div>

            {/* Legend */}
            <div className="mb-4 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
              <LegendDot color="bg-green-600/80" label={`This ${type}'s position`} />
              <LegendDot color="bg-orange-500" label="Remove at current position" />
              <LegendDot color="bg-red-600/80" label="Position taken" />
              <LegendDot color="bg-gray-600/70" label="Position empty" />
              <LegendDot color="bg-blue-600" rounded label="Selected position" />
              <p className="col-span-2 mt-1 text-[11px] leading-5 text-gray-600">
                <span className="font-semibold">Notice:</span> Placing an item on an
                occupied slot will override the previous item. Selecting the current
                item’s position will remove it and leave the slot empty.
              </p>
            </div>

            {/* Grid of slots */}
            <div className="grid grid-cols-4 gap-3">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((val) => (
                <SlotButton
                  key={val}
                  val={val}
                  status={
                    localPosition
                      ? isPositionSelected(localPosition, val, type, id)
                      : null
                  }
                  selectedPosition={selectedPosition}
                  setSelectedPosition={setSelectedPosition}
                />
              ))}
            </div>

            {/* Footer */}
            <div className="mt-5 w-full">
              <span
                className={[
                  "my-2 block text-xs font-semibold",
                  error ? "text-red-600" : success ? "text-emerald-600" : "hidden",
                ].join(" ")}
              >
                {error ? "An error occurred!" : "Success"}
              </span>

              {selectedPosition ? (
                <button
                  type="button"
                  onClick={() => (loading ? null : save())}
                  className="group relative inline-flex w-full items-center justify-center overflow-hidden rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                >
                  <span className="absolute inset-0 -z-10 bg-gradient-to-r from-emerald-500 via-amber-500 to-purple-500 opacity-0 transition group-hover:opacity-100" />
                  {loading ? (
                    <AiOutlineLoading className="h-5 w-5 animate-spin" />
                  ) : (
                    "Save changes"
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  className="inline-flex w-full cursor-not-allowed items-center justify-center rounded-xl bg-gray-300 px-4 py-2.5 text-sm font-semibold text-white"
                  disabled
                >
                  No position selected
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- Small subcomponents (visual only) ---------- */

const LegendDot = ({ color, label, rounded = false }) => (
  <p className="flex items-center gap-2 text-gray-700">
    <span
      className={[
        rounded ? "h-3 w-5 rounded-md" : "h-3 w-3 rounded-full",
        color,
        "inline-block",
      ].join(" ")}
    />
    <span>{label}</span>
  </p>
);

const SlotButton = ({ val, status, setSelectedPosition, selectedPosition }) => {
  // Visual states:
  // - status === true && selected === val -> orange (remove at current)
  // - selected === val -> blue (selected)
  // - status === true -> green (this item's current position)
  // - status === false -> red (taken)
  // - else -> gray (empty)
  const isSelected = selectedPosition === val;

  const classes =
    status === true && isSelected
      ? "bg-orange-500 hover:bg-orange-600 text-white"
      : isSelected
        ? "bg-blue-600 hover:bg-blue-700 text-white"
        : status === true
          ? "bg-green-600/80 hover:bg-green-600 text-white"
          : status === false
            ? "bg-red-700 hover:bg-red-800 text-white"
            : "bg-gray-600/70 hover:bg-gray-700 text-white";

  return (
    <button
      type="button"
      onClick={() => setSelectedPosition(isSelected ? 0 : val)}
      className={[
        "inline-flex h-10 w-full items-center justify-center rounded-lg",
        "text-sm font-semibold transition shadow-sm hover:shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500",
        classes,
      ].join(" ")}
      aria-pressed={isSelected ? "true" : "false"}
      aria-label={`Position ${val}`}
    >
      {val}
    </button>
  );
};
